import { useEffect, useRef, useState, useCallback } from 'react'
import { io, Socket } from 'socket.io-client'
import { getAccessToken } from '@/api/client'
import { useAuthStore } from '@/store/authStore'

type EventCallback = (data: unknown) => void

interface UseSocketReturn {
  socket: Socket | null
  connected: boolean
  on: (event: string, callback: EventCallback) => void
  off: (event: string, callback: EventCallback) => void
  emit: (event: string, data?: unknown) => void
  joinRoom: (room: string) => void
  leaveRoom: (room: string) => void
}

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || ''

export function useSocket(): UseSocketReturn {
  const { isAuthenticated, user } = useAuthStore()
  const socketRef = useRef<Socket | null>(null)
  const [connected, setConnected] = useState(false)
  const eventHandlers = useRef<Map<string, Set<EventCallback>>>(new Map())

  useEffect(() => {
    if (!isAuthenticated || !user) {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
        setConnected(false)
      }
      return
    }

    const token = getAccessToken()
    if (!token) return

    // Create socket connection — backend mounts socket.io at /ws
    const socket = io(SOCKET_URL, {
      path: '/ws/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
    })

    socketRef.current = socket

    socket.on('connect', () => {
      setConnected(true)

      // Join tenant room if available
      if (user.tenantId) {
        socket.emit('join:tenant', { tenantId: user.tenantId })
      }

      // Join user-specific room
      socket.emit('join:user', { userId: user.id })
    })

    socket.on('disconnect', () => {
      setConnected(false)
    })

    socket.on('connect_error', (error: Error) => {
      console.warn('[Socket] Connection error:', error.message)
      setConnected(false)
    })

    socket.on('reconnect', (attemptNumber: number) => {
      console.log('[Socket] Reconnected after', attemptNumber, 'attempts')
      setConnected(true)

      // Re-join rooms after reconnect
      if (user.tenantId) {
        socket.emit('join:tenant', { tenantId: user.tenantId })
      }
      socket.emit('join:user', { userId: user.id })
    })

    // Re-attach all registered event handlers to the new socket
    eventHandlers.current.forEach((callbacks, event) => {
      callbacks.forEach((cb) => {
        socket.on(event, cb as (...args: unknown[]) => void)
      })
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
      setConnected(false)
    }
  }, [isAuthenticated, user])

  const on = useCallback((event: string, callback: EventCallback) => {
    // Track handler
    if (!eventHandlers.current.has(event)) {
      eventHandlers.current.set(event, new Set())
    }
    eventHandlers.current.get(event)!.add(callback)

    // Attach to live socket
    if (socketRef.current) {
      socketRef.current.on(event, callback as (...args: unknown[]) => void)
    }
  }, [])

  const off = useCallback((event: string, callback: EventCallback) => {
    const handlers = eventHandlers.current.get(event)
    if (handlers) {
      handlers.delete(callback)
    }

    if (socketRef.current) {
      socketRef.current.off(event, callback as (...args: unknown[]) => void)
    }
  }, [])

  const emit = useCallback((event: string, data?: unknown) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data)
    }
  }, [])

  const joinRoom = useCallback((room: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('join:room', { room })
    }
  }, [])

  const leaveRoom = useCallback((room: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave:room', { room })
    }
  }, [])

  return {
    socket: socketRef.current,
    connected,
    on,
    off,
    emit,
    joinRoom,
    leaveRoom,
  }
}
